import axios from "axios";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

// Auth is carried by an httpOnly `access_token` cookie (set by the backend on
// login/register/google). withCredentials sends it automatically; no token is
// ever stored in JS-readable storage, closing the XSS token-theft vector.
const api = axios.create({ baseURL: API, withCredentials: true });

// When the trial has expired the backend returns 402 — broadcast it so the shell
// can raise the lockout overlay immediately (in addition to the periodic poll).
api.interceptors.response.use(
  (r) => r,
  (err) => {
    if (err?.response?.status === 402 && err.response.data?.trial_expired) {
      window.dispatchEvent(new CustomEvent("trial-expired"));
    }
    return Promise.reject(err);
  }
);

export function formatApiError(detail) {
  if (detail == null) return "Something went wrong. Please try again.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail))
    return detail.map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e))).filter(Boolean).join(" ");
  if (detail && typeof detail.msg === "string") return detail.msg;
  return String(detail);
}

export async function downloadFile(path, filename) {
  const res = await api.get(path, { responseType: "blob" });
  const url = window.URL.createObjectURL(res.data);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  window.URL.revokeObjectURL(url);
}

// Shared gate for transactional "act" actions (book / quote / order / ride). Guests are sent to
// login and unverified customers to mobile verification — both carrying a `next` return path so
// the original action resumes after. Returns true when handled (caller shows nothing more).
export function handleActError(e, navigate) {
  const status = e?.response?.status;
  const detail = e?.response?.data?.detail;
  const next = encodeURIComponent(window.location.pathname + window.location.search);
  if (status === 401) {
    toast.error("Please sign in to continue", { description: "Log in or create an account — we'll bring you right back." });
    navigate(`/login?next=${next}`);
    return true;
  }
  if (status === 403 && detail === "mobile_verification_required") {
    toast.error("Verify your mobile number to continue", { description: "One quick step — we'll return you here right after." });
    navigate(`/verify-mobile?next=${next}`);
    return true;
  }
  return false;
}

export default api;
